import { useApi } from '@/api';
import { ChartSkeleton, ChartTimeFilter } from '@/components';
import { useChartPeriod } from '@/hooks';
import { tokensMapping } from '@/utils/tokens';
import {
  CurrencyValue,
  DisplaySwitch,
  Flex,
  formatNumber,
  formatPercentage,
  LineChart
} from '@blockanalitica/ui';
import { useMemo, useState } from 'react';

export default function HistoricTabs() {
  const [activeDisplayOption, setActiveDisplayOption] = useState('total');
  const [chartDaysAgo, setChartDaysAgo] = useState('30');
  const [, updateChartConfigWithPeriod] = useChartPeriod(chartDaysAgo);
  const config = updateChartConfigWithPeriod();
  const timeFilter = useMemo(
    () => (
      <ChartTimeFilter activeOption={chartDaysAgo} onChange={setChartDaysAgo} />
    ),
    [chartDaysAgo]
  );
  const displayOptions = useMemo(
    () => [
      { key: 'total', value: 'Total supply' },
      { key: 'borrow', value: 'Total Borrow' },
      { key: 'rate', value: 'Supply APY' },
      { key: 'utilization_rate_24h', value: 'Utilization' }
    ],
    []
  );

  const [data, error] = useApi({
    url: `/stusds/historic/`,
    queryParams: {
      days_ago: chartDaysAgo
    }
  });

  const datasets = useMemo(() => {
    return [
      {
        xField: 'date',
        yField: activeDisplayOption
      }
    ];
  }, [activeDisplayOption]);

  const yScaleValueFormatter = (value) => {
    switch (activeDisplayOption) {
      case 'rate':
      case 'utilization_rate_24h':
        return formatPercentage(Number(value));
      default:
        return formatNumber(Number(value));
    }
  };

  const valueFormatter = useMemo(() => {
    function formatValue(value: string) {
      switch (activeDisplayOption) {
        case 'rate':
        case 'utilization_rate_24h':
          return formatPercentage(Number(value));
        case 'borrow':
          return (
            <CurrencyValue
              value={value}
              options={{
                currency: tokensMapping('USDS'),
                iconSize: '3'
              }}
            />
          );

        default:
          return (
            <CurrencyValue
              value={value}
              options={{
                currency: tokensMapping('stUSDS'),
                iconSize: '3'
              }}
            />
          );
      }
    }
    return formatValue;
  }, [activeDisplayOption]);

  if (error) {
    throw error;
  }

  if (data) {
    return (
      <Flex flexDirection="column" gap="xs">
        <DisplaySwitch
          data={displayOptions}
          activeOption={activeDisplayOption}
          onChange={setActiveDisplayOption}
        />

        <LineChart
          key={activeDisplayOption}
          data={data.historic}
          valueFormatter={valueFormatter}
          datasets={datasets}
          yScaleValueFormatter={yScaleValueFormatter}
          timeFilter={timeFilter}
          config={config}
        />
      </Flex>
    );
  }

  return <ChartSkeleton />;
}
